import React, { useState, useEffect } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-regular-svg-icons';
import PortalTooltip from '../../../../components/PortalTooltip/PortalTooltip';
import VideoPlayerPopup from '../../../../containers/VideoPlayerPopup/VideoPlayerPopup';
import { FILE_EXTENSIONS } from '../../../../utils/filesConstants';
import '../../../../components/DocumentListCard/DocumentListCard.scss';
import '../../../../components/DocumentTile/DocumentTile.scss';
import './ConnectMarketplaceTemplates.scss';

interface SampleTemplate {
    id: number;
    name: string;
    fileName?: string;
    fileUrl?: string;
    uploadedAt: string;
}

const ConnectMarketplaceTemplates = () => {
    const axios = useStateSelector((state) => state.core.axios);
    const [templates, setTemplates] = useState<SampleTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [isVideoPopupVisible, setIsVideoPopupVisible] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [videoTitle, setVideoTitle] = useState<string>('');

    const getTemplates = () => {
        setIsLoading(true);
        setTemplates([]);

        axios
            .get('/api/Marketplace/MarketplaceSampleTemplates', {
                params: {
                    pageNumber: 1,
                    pageSize: 100,
                },
            })
            .then((response) => {
                if (response.status === 200) {
                    const templateList: SampleTemplate[] =
                        response.data.list || response.data.items || [];
                    setTemplates(templateList);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDownload = (template: SampleTemplate) => {
        setDownloadingId(template.id);

        let fileNameForCheck =
            template.fileUrl || template.fileName || template.name;
        if (fileNameForCheck && fileNameForCheck.includes('/')) {
            fileNameForCheck =
                fileNameForCheck.split('/').pop() || fileNameForCheck;
        }
        const isVideo = isVideoFile(fileNameForCheck);

        axios
            .get(`/api/Marketplace/MarketplaceSampleTemplates/${template.id}`)
            .then((response) => {
                console.log('API Response:', response);
                if (response.status === 200) {
                    const downloadUrl = response.data.url || response.data;

                    if (downloadUrl) {
                        if (isVideo) {
                            console.log(
                                'Opening video popup with URL:',
                                downloadUrl
                            );
                            setVideoUrl(downloadUrl);
                            setVideoTitle(template.name);
                            setIsVideoPopupVisible(true);
                        } else {
                            window.open(downloadUrl, '_blank');
                        }
                    }
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setDownloadingId(null);
            });
    };

    const getTooltipId = (templateId: number) => {
        return `template-tooltip-${templateId}`;
    };

    const isVideoFile = (fileName: string): boolean => {
        if (!fileName) {
            return false;
        }
        const extension = '.' + fileName.split('.').pop()?.toLowerCase();
        const videoFileType = FILE_EXTENSIONS.find((ft) => ft.name === 'Video');
        const isVideo = videoFileType?.extensions?.includes(extension) || false;
        return isVideo;
    };

    useEffect(() => {
        getTemplates();
    }, []);

    return (
        <div className="connect-marketplace-templates">
            <div className="documents-container">
                {isLoading ? (
                    <div className="loading-message">Loading...</div>
                ) : templates.length > 0 ? (
                    templates.map((template) => (
                        <div
                            key={`document-${template.id}-${template.name}`}
                            style={{ width: 142, height: 60 }}
                            className={`document-tile ${
                                downloadingId === template.id
                                    ? 'downloading'
                                    : ''
                            }`}
                            onClick={() => handleDownload(template)}>
                            <div className="icon-container">
                                <FontAwesomeIcon
                                    inverse
                                    className="icon"
                                    icon={faFileAlt as any}
                                />
                            </div>
                            <div className="file-info">
                                <div
                                    className="file-name"
                                    id={getTooltipId(template.id)}>
                                    {template.name}
                                </div>
                                <PortalTooltip
                                    anchorId={getTooltipId(template.id)}
                                    delayShow={300}
                                    place="top"
                                    content={template.name}
                                />
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-templates-message">
                        No templates available
                    </div>
                )}
            </div>
            {isVideoPopupVisible && videoUrl ? (
                <VideoPlayerPopup
                    title={videoTitle}
                    src={videoUrl}
                    onClose={() => {
                        setIsVideoPopupVisible(false);
                        setVideoUrl(null);
                        setVideoTitle('');
                    }}
                    isRecordingEnabled={false}
                    hideContextMenu={true}
                />
            ) : null}
        </div>
    );
};

export default ConnectMarketplaceTemplates;
